/**
 * Canvas Signature Utilities
 *
 * Provides proper DPI-aware canvas initialization and coordinate transformation
 * for signature capture. Fixes common canvas drawing offset issues on high-DPI
 * displays (Retina, 4K, etc.).
 *
 * Key Features:
 * - Accounts for devicePixelRatio (1x, 1.5x, 2x, 3x displays)
 * - Proper coordinate transformation from screen space to canvas space
 * - Prevents common offset bugs caused by scale mismatches
 */

export interface CanvasContext {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  scale: number;
  displayWidth: number;
  displayHeight: number;
}

/**
 * Initialize a canvas element for signature capture with proper DPI scaling
 *
 * @param canvas - The HTML canvas element
 * @param displayWidth - Desired display width in CSS pixels
 * @param displayHeight - Desired display height in CSS pixels (default: 200)
 * @returns CanvasContext object with canvas, context, and scale information
 */
export function initializeSignatureCanvas(
  canvas: HTMLCanvasElement,
  displayWidth: number,
  displayHeight: number = 200
): CanvasContext {
  const dpr = window.devicePixelRatio || 1;

  // Set internal canvas resolution (accounts for high-DPI displays)
  // This ensures crisp rendering on Retina and 4K screens
  canvas.width = displayWidth * dpr;
  canvas.height = displayHeight * dpr;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get 2D canvas context');
  }

  // DON'T use ctx.scale() - we'll manually scale coordinates instead
  // This is clearer and avoids coordinate space confusion

  // Apply default drawing styles for signatures (scaled for physical pixels)
  ctx.strokeStyle = '#000';
  ctx.lineWidth = 2 * dpr; // Scale line width for DPI
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Clear canvas with white background
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height); // Use physical dimensions

  return {
    canvas,
    ctx,
    scale: dpr,
    displayWidth,
    displayHeight
  };
}

/**
 * Get canvas coordinates from a mouse or touch event
 *
 * Properly transforms screen coordinates to canvas coordinates accounting for:
 * - Canvas position on screen (getBoundingClientRect)
 * - Scale difference between display size and internal resolution
 * - Device pixel ratio
 *
 * @param event - React mouse or touch event
 * @param canvas - The canvas element
 * @returns Object with x and y coordinates in physical canvas space
 */
export function getCanvasCoordinates(
  event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>,
  canvas: HTMLCanvasElement
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();

  // Get client coordinates from mouse or touch event
  const clientX = 'touches' in event
    ? event.touches[0].clientX
    : event.clientX;
  const clientY = 'touches' in event
    ? event.touches[0].clientY
    : event.clientY;

  // Calculate offset from canvas edge in display space
  const displayX = clientX - rect.left;
  const displayY = clientY - rect.top;

  // Scale to physical canvas coordinates
  // Canvas internal size / display size gives us the scale factor
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;

  return {
    x: displayX * scaleX,
    y: displayY * scaleY
  };
}

/**
 * Clear the signature canvas back to white background
 *
 * @param ctx - Canvas 2D context
 * @param canvas - The canvas element (to get physical dimensions)
 */
export function clearSignatureCanvas(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement
): void {
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height); // Use physical dimensions
}

/**
 * Generate a PNG data URL from canvas
 *
 * @param canvas - The canvas element
 * @returns PNG data URL suitable for storage/transmission
 */
export function getSignatureDataUrl(canvas: HTMLCanvasElement): string {
  return canvas.toDataURL('image/png');
}
